import { Badge, Box, ColorInput, Group, Stack, Text } from "@mantine/core";

import {
  CONTRAST_BACKGROUNDS,
  MIN_CONTRAST,
  contrastRatio,
  deriveInk,
  inkPassesContrast,
  isHexColor,
} from "@/branding/color";
import { t } from "@/i18n";

/**
 * The lab's primary and ink, with the contrast the server will hold the ink to (B6).
 *
 * The ink is optional: left empty, the server derives it from the primary, and this shows
 * the colour it will derive. A set ink is shown against both page backgrounds with its
 * ratio, and one under 4.5:1 is marked as the save will refuse it.
 */
interface ColorFieldsProps {
  primary: string;
  ink: string;
  onPrimary: (value: string) => void;
  onInk: (value: string) => void;
  /** The parent's colours, shown when this organisation sets none (one unit, D19). */
  inheritedNote: string | null;
  primaryError?: string | null;
  inkError?: string | null;
}

export function ColorFields({
  primary,
  ink,
  onPrimary,
  onInk,
  inheritedNote,
  primaryError,
  inkError,
}: ColorFieldsProps): JSX.Element {
  const primaryValue = primary.trim().toLowerCase();
  const inkValue = ink.trim().toLowerCase();
  const primaryValid = isHexColor(primaryValue);
  const inkSet = inkValue !== "";
  const inkValid = isHexColor(inkValue);
  const effectiveInk = inkSet
    ? inkValid
      ? inkValue
      : null
    : primaryValid
      ? deriveInk(primaryValue)
      : null;

  return (
    <Stack gap="xs">
      <Group grow align="flex-start">
        <ColorInput
          label={t("branding.primary_color")}
          description={t("branding.primary_color_hint")}
          format="hex"
          value={primary}
          onChange={onPrimary}
          error={
            primaryError ??
            (primary.trim() !== "" && !primaryValid ? t("branding.color_format") : null)
          }
          data-testid="branding-primary"
        />
        <ColorInput
          label={t("branding.ink_color")}
          description={t("branding.ink_color_hint")}
          format="hex"
          value={ink}
          onChange={onInk}
          error={inkError ?? (inkSet && !inkValid ? t("branding.color_format") : null)}
          data-testid="branding-ink"
        />
      </Group>
      {inheritedNote !== null && primaryValue === "" && (
        <Text size="sm" c="dimmed">
          {inheritedNote}
        </Text>
      )}
      {effectiveInk !== null && <ContrastReport ink={effectiveInk} derived={!inkSet} />}
    </Stack>
  );
}

function ContrastReport({ ink, derived }: { ink: string; derived: boolean }): JSX.Element {
  const passes = inkPassesContrast(ink);
  return (
    <Stack gap={4} data-testid="branding-contrast">
      <Group gap="xs">
        <Text size="sm">{derived ? t("branding.ink_derived") : t("branding.ink_set")}</Text>
        <Box
          w={18}
          h={18}
          style={{ background: ink, borderRadius: 4, border: "1px solid #ccc" }}
          aria-hidden
        />
        <Text size="sm" ff="monospace">
          {ink}
        </Text>
        <Badge
          color={passes ? "green" : "red"}
          variant="light"
          data-testid="branding-contrast-verdict"
        >
          {passes ? t("branding.contrast_passes") : t("branding.contrast_fails")}
        </Badge>
      </Group>
      <Group gap="md">
        {CONTRAST_BACKGROUNDS.map((bg) => {
          const ratio = contrastRatio(ink, bg);
          return (
            <Box
              key={bg}
              px="sm"
              py={4}
              style={{ background: bg, color: ink, borderRadius: 4, border: "1px solid #ddd" }}
            >
              <Text size="sm" fw={600} c={ink} span>
                {t("branding.contrast_sample")}
              </Text>{" "}
              <Text size="xs" c={ratio >= MIN_CONTRAST ? "#1f1f1f" : "#b00020"} span>
                {ratio.toFixed(2)}:1 {t("branding.contrast_on")} {bg}
              </Text>
            </Box>
          );
        })}
      </Group>
    </Stack>
  );
}
